/**
 * Customily Integration
 * Handles integration with Customily for image uploads
 */
class CustomilyIntegration {
  constructor(options = {}) {
    this.currentCustomilyId = options.currentCustomilyId || 1;
    this.customily_ids = options.customily_ids || [];
    
    // Mobile detection
    this.isMobile = window.innerWidth < 768;
    
    // Listen for resize events to update mobile status
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  }
  
  /**
   * Set the Customily IDs
   * @param {Array} ids 
   */
  setCustomilyIds(ids) {
    this.customily_ids = Array.isArray(ids) ? ids : [];
  }

  /**
   * Waits for Customily input fields to be ready.
   * @param {number} timeout - Maximum time to wait in milliseconds.
   * @param {number} interval - Interval between checks in milliseconds.
   * @returns {Promise<void>}
   */
  async waitForCustomilyReady(timeout = 10000, interval = 500) {
    console.log('Waiting for Customily to be ready...');
    if (!this.customily_ids || this.customily_ids.length === 0) {
      console.warn('No Customily IDs configured. Assuming Customily is ready or not needed.');
      return Promise.resolve();
    }

    const firstInputSelector = `input[type="file"][data-element-id="${this.customily_ids[0]}"]`;

    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const check = () => {
        const inputElement = document.querySelector(firstInputSelector);
        if (inputElement) {
          console.log('Customily is ready.');
          resolve();
        } else if (Date.now() - startTime > timeout) {
          const errorMessage = 'Customily did not become ready in time.';
          console.error(errorMessage);
          reject(new Error(errorMessage));
        } else {
          setTimeout(check, interval);
        }
      };
      check();
    });
  }

  /**
   * Sets featured images on Customily fields upon page load.
   * @param {Array<Object>} featuredImages - Array of featured image objects, each with an image_url property.
   * @returns {Promise<void>}
   */
  async setFeaturedImagesOnLoad(featuredImages) {
    if (!featuredImages || featuredImages.length === 0) {
      console.log('No featured images to set on Customily load.');
      return;
    }

    if (!this.customily_ids || this.customily_ids.length === 0) {
      console.warn('No Customily fields configured for featured images.');
      return;
    }

    console.log(`Attempting to set ${featuredImages.length} featured images on Customily fields.`);

    try {
      await this.waitForCustomilyReady();
    } catch (error) {
      console.error('Failed to set featured images because Customily did not become ready:', error);
      return;
    }
    
    // The existing `uploadToCustomily` method handles the cycling of `currentCustomilyId`.
    // We will call it for each featured image.
    // If there are more featured images than Customily slots, they will cycle and overwrite earlier ones.
    // If there are fewer, only the initial slots will be filled.
    let successfulUploads = 0;
    for (const image of featuredImages) {
      if (image && image.image_url) {
        // Stop if we've already filled each Customily slot once with a featured image
        // to avoid unnecessary overwrites if there are many featured images.
        // This ensures each slot gets at most one *distinct* featured image from this initial load.
        if (successfulUploads >= this.customily_ids.length) {
          console.log('All available Customily fields have been assigned a featured image. Stopping further featured image uploads.');
          break; 
        }
        console.log(`Setting featured image ${image.image_url} to a Customily field.`);
        try {
          const success = await this.uploadToCustomily(image.image_url, false); // Don't open modal on initial load
          if (success) {
            successfulUploads++;
          }
        } catch (error) {
          console.error(`Failed to set featured image ${image.image_url} on Customily:`, error);
          // Continue with the next featured image
        }
      }
    }
    console.log(`Finished setting featured images. Successfully set: ${successfulUploads}.`);
  }

  /**
   * Sets the first image on Customily field upon page load when no featured images exist.
   * @param {Object} firstImage - First image object with an image_url property.
   * @returns {Promise<void>}
   */
  async setFirstImageOnLoad(firstImage) {
    if (!firstImage || !firstImage.image_url) {
      console.log('No first image to set on Customily load.');
      return;
    }

    if (!this.customily_ids || this.customily_ids.length === 0) {
      console.warn('No Customily fields configured for first image.');
      return;
    }

    console.log(`Attempting to set first image ${firstImage.image_url} on Customily field.`);

    try {
      await this.waitForCustomilyReady();
    } catch (error) {
      console.error('Failed to set first image because Customily did not become ready:', error);
      return;
    }
    
    try {
      const success = await this.uploadToCustomily(firstImage.image_url, false); // Don't open modal on initial load
      if (success) {
        console.log(`Successfully set first image ${firstImage.image_url} on Customily field.`);
      } else {
        console.error(`Failed to set first image ${firstImage.image_url} on Customily field.`);
      }
    } catch (error) {
      console.error(`Error setting first image ${firstImage.image_url} on Customily:`, error);
    }
  }
  
  /**
   * Upload an image to Customily fields
   * @param {string} imageUrl URL of the image to upload
   * @param {boolean} shouldOpenModal Whether to open the Customily modal after successful upload
   * @returns {Promise<boolean>} true if successful, false otherwise
   */
  async uploadToCustomily(imageUrl, shouldOpenModal = true) {
    try {
      if (!imageUrl) {
        throw new Error('URL da imagem não fornecido');
      }
      
      if (!this.customily_ids || this.customily_ids.length === 0) {
        console.warn('Nenhum campo Customily configurado');
        return false; // Indicate failure or inaction
      }

      // Ensure Customily is ready before attempting to find the input
      // This is a quick check; waitForCustomilyReady should be called for initial setup.
      const currentInputSelector = `input[type="file"][data-element-id="${this.currentCustomilyId}"]`;
      let input = document.querySelector(currentInputSelector);

      if (!input) {
         // Fallback: if the specific input for currentCustomilyId is not found,
         // try finding the first available one if customily_ids are defined.
         // This could happen if currentCustomilyId is out of sync or the element ID is dynamic based on customily_ids array.
         // Let's assume customily_ids contains the actual element IDs to target.
        const targetCustomilyElementId = this.customily_ids[ (this.currentCustomilyId - 1) % this.customily_ids.length ];
        input = document.querySelector(`input[type="file"][data-element-id="${targetCustomilyElementId}"]`);
        if (!input) {
          console.warn(`Input não encontrado para o ID ${this.currentCustomilyId} (tentativa com ${targetCustomilyElementId}). Verifique a configuração dos IDs Customily.`);
          return false; // Indicate failure
        }
        console.log(`Usando input com ID de elemento ${targetCustomilyElementId} baseado no índice de currentCustomilyId.`);
      }

      console.log('Uploading to Customily:', imageUrl, 'to field ID:', input.dataset.elementId);
      const blob = await fetch(imageUrl).then(r => r.blob());
      const file = new File([blob], "imagem-gerada.jpg", { type: blob.type });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      
      input.files = dataTransfer.files;
      // Dispatching events consistently
      const changeEvent = new Event("change", { bubbles: true });
      const inputEvent = new Event("input", { bubbles: true });

      // It's crucial that the events are dispatched in an order that Customily expects.
      // Typically, 'input' might be listened to for immediate feedback, and 'change' for final confirmation.
      await new Promise((resolve) => {
        // Add a temporary listener to confirm the event was processed if Customily signals it.
        // For now, we assume dispatching is enough.
        input.dispatchEvent(inputEvent); 
        input.dispatchEvent(changeEvent);
        // Add a small delay to allow Customily's event handlers to process
        setTimeout(resolve, 100); 
      });
      
      const previouslyUsedId = input.dataset.elementId;

      // Rotate to the next Customily ID based on the array of configured IDs
      // This ensures we cycle through the *available* and *configured* customily_ids
      const currentIndexInArray = this.customily_ids.indexOf(previouslyUsedId);
      if (currentIndexInArray !== -1 && this.customily_ids.length > 0) {
         this.currentCustomilyId = this.customily_ids[(currentIndexInArray + 1) % this.customily_ids.length];
      } else if (this.customily_ids.length > 0) {
         // Fallback if previouslyUsedId was not in customily_ids (e.g. if it was a direct number)
         // or if customily_ids was just populated.
         // This logic might need refinement based on how `data-element-id` relates to `this.customily_ids`.
         // Assuming `this.customily_ids` are the actual `data-element-id` values.
         // And `this.currentCustomilyId` stores the *next* `data-element-id` to use.
         let currentNumericId = parseInt(this.currentCustomilyId, 10);
         if (isNaN(currentNumericId) && this.customily_ids.length > 0) { // If currentCustomilyId is not a number, start with the first from array
             currentNumericId = parseInt(this.customily_ids[0], 10);
         }

         if (!isNaN(currentNumericId)) {
             let nextNumericId = (currentNumericId % this.customily_ids.length) + 1; // Simple numeric cycle based on length
             // This assumes customily_ids are sequential like 1, 2, 3...
             // A more robust way is to cycle through the `this.customily_ids` array itself.
             // The logic above (indexOf) is better if `this.customily_ids` contains the actual string IDs.

             // Corrected logic: `this.currentCustomilyId` should store the *next element ID string* from `this.customily_ids`
             const currentIdForLogic = previouslyUsedId; // The ID that was just used
             const indexOfUsedId = this.customily_ids.indexOf(currentIdForLogic);
             
             if (indexOfUsedId !== -1) { // If the used ID is in our list
                 this.currentCustomilyId = this.customily_ids[(indexOfUsedId + 1) % this.customily_ids.length];
             } else if (this.customily_ids.length > 0) { // Fallback: If used ID wasn't in list, or list just populated
                 this.currentCustomilyId = this.customily_ids[0]; // Default to the first one
             }
             // If this.customily_ids is empty, this.currentCustomilyId remains unchanged.
         }
      }

      console.log(`Arte aplicada com sucesso no campo ${previouslyUsedId}! Próximo ID Customily: ${this.currentCustomilyId}`);
      
      // Open Customily modal after successful image upload only if shouldOpenModal is true AND is mobile
      if (shouldOpenModal && this.isMobile) {
        const previewButton = document.getElementById('customily-preview-button');
        if (previewButton) {
          console.log('[CustomilyIntegration] 📱 Opening Customily modal after successful image upload (Mobile)');
          previewButton.click();
        } else {
          console.warn('[CustomilyIntegration] ⚠️ Customily preview button not found');
        }
      } else if (shouldOpenModal && !this.isMobile) {
        console.log('[CustomilyIntegration] 💻 Modal opening skipped - desktop device');
      } else {
        console.log('[CustomilyIntegration] ℹ️ Modal opening skipped - initial load or manual override');
      }
      
      return true; // Indicate success
    } catch (error) {
      console.error('Error uploading to Customily:', error);
      // Do not re-throw, allow other operations to continue
      return false; // Indicate failure
    }
  }
}

// Export the class
window.CustomilyIntegration = CustomilyIntegration;
